import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../../lib/supabase';
import AppointmentCalendar from './AppointmentCalendar';

interface Appointment {
  id: string;
  nombre: string;
  telefono: string;
  email: string;
  modelo_coche: string;
  matricula: string;
  servicio: string;
  notas: string;
  fecha: string;
  hora: string;
  estado: string;
  origen: string;
  created_at: string;
}

const ESTADO_CONFIG: Record<string, { label: string; cls: string }> = {
  pendiente:   { label: 'Pendiente',   cls: 'bg-yellow-400/10 text-yellow-400 border-yellow-400/30' },
  confirmada:  { label: 'Confirmada',  cls: 'bg-green-500/10 text-green-400 border-green-500/30' },
  completada:  { label: 'Completada',  cls: 'bg-zinc-700 text-zinc-300 border-zinc-600' },
  cancelada:   { label: 'Cancelada',   cls: 'bg-red-500/10 text-red-400 border-red-500/30' },
};

const EMPTY_FORM = { nombre: '', telefono: '', email: '', modelo_coche: '', matricula: '', servicio: '', notas: '', fecha: '', hora: '' };

interface Props {
  onBadgeUpdate: (pending: number) => void;
}

const AppointmentsTab = ({ onBadgeUpdate }: Props) => {
  const [apts, setApts] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'lista' | 'calendario'>('lista');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [reminderStatus, setReminderStatus] = useState<Record<string, 'ok' | 'err' | 'no_email' | 'loading'>>({});

  const sendWhatsAppReminder = (apt: Appointment) => {
    if (!apt.telefono) {
      setReminderStatus((prev) => ({ ...prev, [apt.id]: 'no_email' }));
      setTimeout(() => setReminderStatus((prev) => { const n = { ...prev }; delete n[apt.id]; return n; }), 3000);
      return;
    }
    const phone = apt.telefono.replace(/\D/g, '');
    const fecha = apt.fecha
      ? new Date(apt.fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
      : apt.fecha;
    const mensaje = `Hola ${apt.nombre}, le recordamos que tiene una cita en el taller el ${fecha} a las ${apt.hora}. Por favor, confirme su asistencia respondiendo a este mensaje. ¡Gracias!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(mensaje)}`, '_blank');
    setReminderStatus((prev) => ({ ...prev, [apt.id]: 'ok' }));
    setTimeout(() => setReminderStatus((prev) => { const n = { ...prev }; delete n[apt.id]; return n; }), 3000);
  };

  const fetchApts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('TALLERMECANICOKEVIN_appointments')
      .select('*')
      .order('fecha', { ascending: false })
      .order('hora', { ascending: false });
    const list = data ?? [];
    setApts(list);
    onBadgeUpdate(list.filter((a) => a.estado === 'pendiente').length);
    setLoading(false);
  };

  useEffect(() => { fetchApts(); }, []);

  const updateEstado = async (id: string, estado: string) => {
    await supabase.from('TALLERMECANICOKEVIN_appointments').update({ estado }).eq('id', id);
    setApts((prev) => {
      const updated = prev.map((a) => a.id === id ? { ...a, estado } : a);
      onBadgeUpdate(updated.filter((a) => a.estado === 'pendiente').length);
      return updated;
    });
  };

  const deleteApt = async (id: string) => {
    if (!confirm('¿Eliminar esta cita?')) return;
    await supabase.from('TALLERMECANICOKEVIN_appointments').delete().eq('id', id);
    setApts((prev) => {
      const updated = prev.filter((a) => a.id !== id);
      onBadgeUpdate(updated.filter((a) => a.estado === 'pendiente').length);
      return updated;
    });
    if (expanded === id) setExpanded(null);
  };



  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await supabase.from('TALLERMECANICOKEVIN_appointments').insert({ ...form, estado: 'pendiente', origen: 'manual' });
    setShowModal(false);
    setForm(EMPTY_FORM);
    setSaving(false);
    await fetchApts();
  };

  const counts = { total: apts.length, pendiente: 0, confirmada: 0, completada: 0, cancelada: 0 };
  for (const a of apts) if (a.estado in counts) counts[a.estado as keyof typeof counts]++;

  return (
    <div>
      {/* Stats - 2 cols mobile, 5 desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {Object.entries(counts).map(([k, v]) => (
          <div key={k} className="bg-zinc-900 border border-zinc-800 p-3 md:p-4 rounded-lg text-center">
            <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1 capitalize">{k}</p>
            <p className="text-white font-black text-xl">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 mb-4">
        <div className="flex gap-1 bg-zinc-900 border border-zinc-800 p-1 rounded-lg">
          {(['lista', 'calendario'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap rounded-md ${view === v ? 'bg-yellow-400 text-black' : 'text-zinc-400 hover:text-white'}`}>
              <i className={`${v === 'lista' ? 'ri-list-check' : 'ri-calendar-line'} mr-1.5`} />{v === 'lista' ? 'Lista' : 'Calendario'}
            </button>
          ))}
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap rounded-lg">
          <i className="ri-add-line" /> Nueva cita
        </button>
      </div>

      {view === 'calendario' ? (
        <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6 rounded-lg">
          <AppointmentCalendar appointments={apts} />
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center h-40"><i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" /></div>
      ) : apts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-zinc-600">
          <i className="ri-calendar-line text-3xl mb-2" />
          <p className="text-sm">Sin citas registradas</p>
        </div>
      ) : (
        <div className="space-y-2">
          {apts.map((apt) => {
            const cfg = ESTADO_CONFIG[apt.estado] ?? ESTADO_CONFIG.pendiente;
            const rs = reminderStatus[apt.id];
            return (
              <div key={apt.id} className="border border-zinc-800 bg-zinc-900 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 p-3 md:p-4 cursor-pointer hover:bg-zinc-800/40 transition-colors" onClick={() => setExpanded(expanded === apt.id ? null : apt.id)}>
                  <div className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded ${cfg.cls}`}>{cfg.label}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{apt.nombre || '—'}</p>
                    <p className="text-zinc-500 text-xs truncate">{apt.fecha} · {apt.hora} · {apt.servicio || '—'}</p>
                  </div>
                  <i className={`ri-arrow-${expanded === apt.id ? 'up' : 'down'}-s-line text-zinc-500 text-base flex-shrink-0`} />
                </div>

                {expanded === apt.id && (
                  <div className="border-t border-zinc-800 p-3 md:p-4 space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                      {[
                        ['Teléfono', apt.telefono], ['Email', apt.email],
                        ['Vehículo', apt.modelo_coche], ['Matrícula', apt.matricula],
                        ['Origen', apt.origen], ['Creado', new Date(apt.created_at).toLocaleDateString('es-ES')],
                      ].map(([l, v]) => (
                        <div key={l} className="bg-zinc-800/40 rounded-lg p-2.5">
                          <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-0.5">{l}</p>
                          <p className="text-zinc-300 text-xs font-medium break-all">{v || '—'}</p>
                        </div>
                      ))}
                    </div>
                    {apt.notas && (
                      <div className="bg-zinc-800/50 border-l-2 border-yellow-400/40 p-3 rounded-r-lg">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-wider mb-1">Notas</p>
                        <p className="text-zinc-200 text-sm">{apt.notas}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={apt.estado}
                        onChange={(e) => updateEstado(apt.id, e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 text-white text-xs px-3 py-2 outline-none cursor-pointer rounded-lg"
                      >
                        {Object.keys(ESTADO_CONFIG).map((s) => <option key={s} value={s}>{ESTADO_CONFIG[s].label}</option>)}
                      </select>
                      {apt.telefono && (
                        <a href={`https://wa.me/${apt.telefono.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(apt.nombre)}%2C%20te%20contactamos%20desde%20el%20taller`}
                          target="_blank" rel="nofollow noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider whitespace-nowrap rounded-lg">
                          <i className="ri-whatsapp-line" /> WhatsApp
                        </a>
                      )}
                      {apt.email && (
                        <a href={`mailto:${apt.email}`}
                          className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-wider whitespace-nowrap rounded-lg">
                          <i className="ri-mail-line" /> Email
                        </a>
                      )}
                      <button
                        onClick={() => sendWhatsAppReminder(apt)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap rounded-lg ${
                          rs === 'ok' ? 'bg-green-500/20 text-green-400' :
                          rs === 'no_email' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                        }`}
                      >
                        <i className={`${rs === 'ok' ? 'ri-check-line' : rs === 'no_email' ? 'ri-phone-off-line' : 'ri-whatsapp-line'}`} />
                        <span className="hidden sm:inline">{rs === 'ok' ? 'Enviado' : rs === 'no_email' ? 'Sin teléfono' : 'Recordatorio'}</span>
                      </button>
                      <button onClick={() => deleteApt(apt.id)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap rounded-lg">
                        <i className="ri-delete-bin-line" /> <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="bg-zinc-900 border border-zinc-700 w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-xl">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-900 z-10">
              <h3 className="text-white font-bold text-sm uppercase tracking-wider">Nueva cita</h3>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer rounded-lg"><i className="ri-close-line text-lg" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { name: 'nombre', label: 'Nombre *', required: true },
                  { name: 'telefono', label: 'Teléfono', required: false },
                  { name: 'email', label: 'Email', required: false, type: 'email' },
                  { name: 'modelo_coche', label: 'Vehículo', required: false },
                  { name: 'matricula', label: 'Matrícula', required: false },
                  { name: 'servicio', label: 'Servicio', required: false },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">{f.label}</label>
                    <input
                      name={f.name} required={f.required} type={f.type ?? 'text'}
                      value={form[f.name as keyof typeof form]}
                      onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 outline-none focus:border-yellow-400/50 rounded-lg"
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Fecha *</label>
                  <input type="date" required value={form.fecha} onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 outline-none focus:border-yellow-400/50 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Hora *</label>
                  <input type="time" required value={form.hora} onChange={(e) => setForm((p) => ({ ...p, hora: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 outline-none focus:border-yellow-400/50 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Notas</label>
                <textarea rows={2} maxLength={500} value={form.notas} onChange={(e) => setForm((p) => ({ ...p, notas: e.target.value }))}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 outline-none focus:border-yellow-400/50 resize-none rounded-lg" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 py-3 border border-zinc-700 text-zinc-400 text-xs font-bold uppercase tracking-wider hover:text-white hover:border-zinc-500 transition-colors cursor-pointer whitespace-nowrap rounded-lg">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-3 bg-yellow-400 text-black text-xs font-black uppercase tracking-wider hover:bg-yellow-300 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap rounded-lg">
                  {saving ? 'Guardando…' : 'Crear cita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentsTab;
